import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  BarChart as BarChartIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-basic-dist';
import axios from 'axios';
import { useTranslation } from '../utils/translations';

const Plot = createPlotlyComponent(Plotly);
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// State name to abbreviation mapping
const stateNameToAbbr = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY', 'District of Columbia': 'DC'
};

// Function to convert state names to abbreviations
const convertStatesToAbbr = (stateNames) => {
  return stateNames.map(stateName => stateNameToAbbr[stateName] || stateName);
};

const RawDataVisualization = ({ sidebarOpen, language }) => {
  const t = useTranslation(language);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [plotRevision, setPlotRevision] = useState(0);
  const [selectedChart, setSelectedChart] = useState('samples-per-state');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [chartReady, setChartReady] = useState(false);

  // Chart options - removed avg pop, avg income, education distribution, unique zips per state
  const chartOptions = [
    { value: 'samples-per-state', label: t('totalSamplesPerState') },
    { value: 'sample-count-distribution', label: t('sampleCountDistribution') },
    { value: 'age-distribution', label: t('ageDistribution') },
    { value: 'race-distribution', label: t('raceDistribution') },
    { value: 'gender-distribution', label: t('genderDistribution') },
    { value: 'swing-states-zips', label: t('swingStatesZips') }
  ];

  // Force plot to resize when sidebar state changes with proper debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPlotRevision(prev => prev + 1);
      // Small delay before triggering resize to allow container to settle
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    }, 300); // Wait for sidebar animation to complete

    return () => clearTimeout(timeoutId);
  }, [sidebarOpen]);

  // Fetch data when selected chart changes
  useEffect(() => {
    // Small delay to ensure proper initialization
    const timer = setTimeout(() => {
      fetchChartData();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [selectedChart]);

  const fetchChartData = useCallback(async () => {
    setLoading(true);
    setError('');
    setChartReady(false);

    try {
      let response;
      
      switch (selectedChart) {
        case 'samples-per-state':
          response = await axios.get(`${API_BASE_URL}/raw-data/samples-per-state`);
          break;
        case 'zips-covered-per-state':
          response = await axios.get(`${API_BASE_URL}/raw-data/zips-covered-per-state`);
          break;
        case 'sample-count-distribution':
          response = await axios.get(`${API_BASE_URL}/raw-data/sample-count-distribution`);
          break;
        case 'age-distribution':
          response = await axios.get(`${API_BASE_URL}/raw-data/age-distribution`);
          break;
        case 'race-distribution':
          response = await axios.get(`${API_BASE_URL}/raw-data/race-distribution`);
          break;
        case 'gender-distribution':
          response = await axios.get(`${API_BASE_URL}/raw-data/gender-distribution`);
          break;
        case 'education-distribution':
          response = await axios.get(`${API_BASE_URL}/raw-data/education-distribution`);
          break;
        case 'swing-states-zips':
          response = await axios.get(`${API_BASE_URL}/raw-data/swing-states-zips`);
          break;
        case 'avg-population-by-state':
          response = await axios.get(`${API_BASE_URL}/raw-data/avg-population-by-state`);
          break;
        case 'avg-income-by-state':
          response = await axios.get(`${API_BASE_URL}/raw-data/avg-income-by-state`);
          break;
        default:
          throw new Error('Invalid chart type');
      }
      
      setChartData(response.data);
      // Small delay to ensure data is processed before rendering
      setTimeout(() => {
        setChartReady(true);
      }, 50);
    } catch (err) {
      setError('Failed to fetch chart data. Please try again.');
      console.error('Error fetching chart data:', err);
      setChartReady(false);
    } finally {
      setLoading(false);
    }
  }, [selectedChart]);

  const handleChartChange = (event) => {
    setSelectedChart(event.target.value);
    setChartReady(false); // Reset chart ready state when switching charts
    setPlotRevision(prev => prev + 1); // Force chart re-render
  };

  const createChart = () => {
    if (!chartData || loading || !chartReady) return null;

    let data, layout;
    
    // Safety check to prevent undefined access
    try {

    switch (selectedChart) {
      case 'samples-per-state':
        data = [{
          type: 'bar',
          x: convertStatesToAbbr(chartData.states),
          y: chartData.sample_counts,
          hoverinfo: 'x+y',
          marker: {
            color: '#dc2626',
            opacity: 0.8
          },
          name: 'Samples',
          hovertemplate: '<b>%{x}</b><br>' +
                        'Samples: <b>%{y:,}</b>' +
                        '<extra></extra>'
        }];
        layout = {
          title: {
            text: t('chartTotalSamplesPerState'),
            font: { family: '"Playfair Display", serif', size: 18, color: isDarkMode ? '#e2e8f0' : '#2c2c2c' }
          },
          hovermode: 'closest',
          xaxis: { 
            title: { text: t('axisState'), font: { family: '"Poppins", sans-serif', size: 13, color: isDarkMode ? '#cbd5e0' : '#4a5568' } },
            tickangle: -45,
            tickfont: { family: '"Poppins", sans-serif', size: 11, color: isDarkMode ? '#a0aec0' : '#718096' }
          },
          yaxis: { 
            title: { text: t('axisNumberOfSamples'), font: { family: '"Poppins", sans-serif', size: 13, color: isDarkMode ? '#cbd5e0' : '#4a5568' } },
            tickfont: { family: '"Poppins", sans-serif', size: 11, color: isDarkMode ? '#a0aec0' : '#718096' }
          },
          showlegend: false,
          datarevision: plotRevision
        };
        break;

      case 'sample-count-distribution':
        console.log('sample_ranges:', chartData.sample_ranges);
        console.log('zip_counts:   ', chartData.zip_counts);
      
        data = [{
          type: 'bar',
          x: chartData.sample_ranges,
          y: chartData.zip_counts,
          marker: {
            color: '#dc2626',
            opacity: 0.8
          },
          name: 'ZIP Codes',
          hovertemplate: '<b>%{x}</b><br>' +
                        'ZIP Codes: <b>%{y:,}</b>' +
                        '<extra></extra>'
        }];
        layout = {
          title: {
            text: t('chartSampleCountDistribution'),
            font: { family: '"Playfair Display", serif', size: 18, color: isDarkMode ? '#e2e8f0' : '#2c2c2c' }
          },
          xaxis: { 
            type: 'category',
            categoryorder: 'array',
            categoryarray: chartData.sample_ranges,
            title: { text: t('axisSampleCountRange'), font: { family: '"Poppins", sans-serif', size: 13, color: isDarkMode ? '#cbd5e0' : '#4a5568' } },
            tickfont: { family: '"Poppins", sans-serif', size: 11, color: isDarkMode ? '#a0aec0' : '#718096' },
            tickmode: 'array', 
            tickvals: chartData.sample_ranges,
            ticktext: chartData.sample_ranges
          },
          yaxis: { 
            title: { text: t('axisNumberOfZipCodes'), font: { family: '"Poppins", sans-serif', size: 13, color: isDarkMode ? '#cbd5e0' : '#4a5568' } },
            tickfont: { family: '"Poppins", sans-serif', size: 11, color: isDarkMode ? '#a0aec0' : '#718096' }
          },
          showlegend: false,
          datarevision: plotRevision
        };
        break;

      case 'age-distribution':
        data = [{
          type: 'bar',
          x: chartData.age_groups,
          y: chartData.counts,
          marker: {
            color: '#dc2626',
            opacity: 0.8
          },
          name: 'Participants',
          hovertemplate: '<b>%{x}</b><br>' +
                        'Participants: <b>%{y:,}</b>' +
                        '<extra></extra>'
        }];
        layout = {
          title: {
            text: t('chartAgeDistributionNationwide'),
            font: { family: '"Playfair Display", serif', size: 18, color: isDarkMode ? '#e2e8f0' : '#2c2c2c' }
          },
          xaxis: { 
            title: { text: t('axisAgeGroup'), font: { family: '"Poppins", sans-serif', size: 13, color: isDarkMode ? '#cbd5e0' : '#4a5568' } },
            tickfont: { family: '"Poppins", sans-serif', size: 11, color: isDarkMode ? '#a0aec0' : '#718096' }
          },
          yaxis: { 
            title: { text: t('axisNumberOfParticipants'), font: { family: '"Poppins", sans-serif', size: 13, color: isDarkMode ? '#cbd5e0' : '#4a5568' } },
            tickfont: { family: '"Poppins", sans-serif', size: 11, color: isDarkMode ? '#a0aec0' : '#718096' }
          },
          showlegend: false,
          datarevision: plotRevision
        };
        break;

      case 'race-distribution':
        data = [{
          type: 'bar',
          x: chartData.race_groups.map(race => t(race)),
          y: chartData.counts,
          marker: {
            color: '#dc2626',
            opacity: 0.8
          },
          name: 'Participants',
          hovertemplate: '<b>%{x}</b><br>' +
                        'Participants: <b>%{y:,}</b>' +
                        '<extra></extra>'
        }];
        layout = {
          title: {
            text: t('chartRaceDistributionNationwide'),
            font: { family: '"Playfair Display", serif', size: 18, color: isDarkMode ? '#e2e8f0' : '#2c2c2c' }
          },
          xaxis: { 
            title: { text: t('axisRaceGroup'), font: { family: '"Poppins", sans-serif', size: 13, color: isDarkMode ? '#cbd5e0' : '#4a5568' } },
            tickfont: { family: '"Poppins", sans-serif', size: 11, color: isDarkMode ? '#a0aec0' : '#718096' }
          },
          yaxis: { 
            title: { text: t('axisNumberOfParticipants'), font: { family: '"Poppins", sans-serif', size: 13, color: isDarkMode ? '#cbd5e0' : '#4a5568' } },
            tickfont: { family: '"Poppins", sans-serif', size: 11, color: isDarkMode ? '#a0aec0' : '#718096' }
          },
          showlegend: false,
          datarevision: plotRevision
        };
        break;

      case 'gender-distribution':
        data = [{
          type: 'bar',
          x: chartData.genders.map(gender => t(gender)),
          y: chartData.counts,
          marker: {
            color: '#dc2626',
            opacity: 0.8
          },
          name: 'Participants',
          hovertemplate: '<b>%{x}</b><br>' +
                        'Participants: <b>%{y:,}</b>' +
                        '<extra></extra>'
        }];
        layout = {
          title: {
            text: t('chartGenderDistributionNationwide'),
            font: { family: '"Playfair Display", serif', size: 18, color: isDarkMode ? '#e2e8f0' : '#2c2c2c' }
          },
          xaxis: { 
            title: { text: t('axisGender'), font: { family: '"Poppins", sans-serif', size: 13, color: isDarkMode ? '#cbd5e0' : '#4a5568' } },
            tickfont: { family: '"Poppins", sans-serif', size: 11, color: isDarkMode ? '#a0aec0' : '#718096' }
          },
          yaxis: { 
            title: { text: t('axisNumberOfParticipants'), font: { family: '"Poppins", sans-serif', size: 13, color: isDarkMode ? '#cbd5e0' : '#4a5568' } },
            tickfont: { family: '"Poppins", sans-serif', size: 11, color: isDarkMode ? '#a0aec0' : '#718096' }
          },
          showlegend: false,
          datarevision: plotRevision
        };
        break;



      case 'swing-states-zips':
        data = [{
          type: 'bar',
          x: convertStatesToAbbr(chartData.swing_states),
          y: chartData.suitable_zips,
          marker: {
            color: '#dc2626',
            opacity: 0.8
          },
          name: 'Suitable ZIPs',
          hovertemplate: '<b>%{x}</b><br>' +
                        'Suitable ZIPs: <b>%{y:,}</b>' +
                        '<extra></extra>'
        }];
        layout = {
          title: {
            text: t('chartSuitableZipsSwingStates'),
            font: { family: '"Playfair Display", serif', size: 18, color: isDarkMode ? '#e2e8f0' : '#2c2c2c' }
          },
          xaxis: { 
            title: { text: t('axisSwingState'), font: { family: '"Poppins", sans-serif', size: 13, color: isDarkMode ? '#cbd5e0' : '#4a5568' } },
            tickangle: -45,
            tickfont: { family: '"Poppins", sans-serif', size: 11, color: isDarkMode ? '#a0aec0' : '#718096' }
          },
          yaxis: { 
            title: { text: t('axisNumberOfSuitableZipCodes'), font: { family: '"Poppins", sans-serif', size: 13, color: isDarkMode ? '#cbd5e0' : '#4a5568' } },
            tickfont: { family: '"Poppins", sans-serif', size: 11, color: isDarkMode ? '#a0aec0' : '#718096' }
          },
          showlegend: false,
          datarevision: plotRevision
        };
        break;



      default:
        return null;
    }

    // Ensure data is valid before rendering
    if (!data || !layout || !Array.isArray(data) || data.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="500px">
          <Typography variant="body1" color="text.secondary">
            {t('chartNoData')}
          </Typography>
        </Box>
      );
    }

    return (
      <Plot
        data={data}
        layout={{
          ...layout,
          height: 500,
          plot_bgcolor: 'rgba(0,0,0,0)',
          paper_bgcolor: 'rgba(0,0,0,0)',
          plot_bgcolor: isDarkMode ? '#373737' : 'rgba(0,0,0,0)',
          paper_bgcolor: isDarkMode ? '#373737' : 'rgba(0,0,0,0)',
          hovermode: 'closest',
          hoverlabel: {
            bgcolor: isDarkMode ? '#374151' : '#ffffff',
            bordercolor: isDarkMode ? '#4b5563' : '#e5e7eb',
            font: { color: isDarkMode ? '#f3f4f6' : '#111827' }
          },
          font: { 
            family: '"Poppins", "Inter", sans-serif',
            size: 13,
            color: isDarkMode ? '#a0aec0' : '#374151'
          },
          titlefont: {
            family: '"Playfair Display", "Poppins", serif',
            size: 18,
            color: isDarkMode ? '#e2e8f0' : '#2c2c2c'
          },
          margin: { l: 80, r: 50, t: 80, b: 80 },
          autosize: true
        }}
        config={{
          responsive: true,
          displayModeBar: true,
          modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
          displaylogo: false,
          staticPlot: false,
          scrollZoom: true
        }}
        onInitialized={(figure) => {
          console.log('plot initialized ➞', figure);
        }}
        onUpdate={(figure) => {          
          console.log('plot updated ➞', figure);
        }}
        onHover={(eventData) => {
          console.log('onHover event data:', eventData);
        }}
        onUnhover={(eventData) => {
         console.log('onUnhover event data:', eventData);
        }}
        style={{ width: '100%', height: '500px' }}
        revision={plotRevision}
        useResizeHandler={true}
      />
    );
    
    } catch (error) {
      console.error('Error creating chart:', error);
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="500px">
          <Typography variant="body1" color="error">
            {t('chartErrorLoading')}
          </Typography>
        </Box>
      );
    }
  };

  const renderStatsCards = () => {
    if (!chartData || !chartReady) return null;

    const formatNumber = (num) => {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toLocaleString();
    };

    const getStatsData = () => {
      switch (selectedChart) {
        case 'samples-per-state':
          return [
            { label: t('totalSamples'), value: formatNumber(chartData.total_data_points), icon: <AnalyticsIcon /> },
            { label: t('statesCovered'), value: chartData.categories, icon: <BarChartIcon /> },
            { label: t('avgPerState'), value: formatNumber(Math.round(chartData.total_data_points / chartData.categories)), icon: <AssessmentIcon /> }
          ];
        case 'sample-count-distribution':
          return [
            { label: t('totalZipCodes'), value: formatNumber(chartData.total_data_points), icon: <AnalyticsIcon /> },
            { label: t('sampleRanges'), value: chartData.categories, icon: <BarChartIcon /> },
            { label: t('avgPerRange'), value: formatNumber(Math.round(chartData.total_data_points / chartData.categories)), icon: <AssessmentIcon /> }
          ];
        case 'age-distribution':
        case 'race-distribution':
        case 'gender-distribution':
          return [
            { label: t('totalParticipants'), value: formatNumber(chartData.total_data_points), icon: <AnalyticsIcon /> },
            { label: t('categories'), value: chartData.categories, icon: <BarChartIcon /> },
            { label: t('avgPerCategory'), value: formatNumber(Math.round(chartData.total_data_points / chartData.categories)), icon: <AssessmentIcon /> }
          ];
        case 'swing-states-zips':
          return [
            { label: t('totalSuitableZips'), value: formatNumber(chartData.total_data_points), icon: <AnalyticsIcon /> },
            { label: t('swingStates'), value: chartData.categories, icon: <BarChartIcon /> },
            { label: t('avgPerState'), value: formatNumber(Math.round(chartData.total_data_points / chartData.categories)), icon: <AssessmentIcon /> }
          ];
        default:
          return [];
      }
    };

    const statsData = getStatsData();

    return (
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {statsData.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ backgroundColor: isDarkMode ? '#373737' : '#ffffff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {stat.icon && React.cloneElement(stat.icon, { color: 'primary', sx: { mr: 1 } })}
                    <Typography variant="h6">{stat.label}</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {stat.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ pb: 5, px: 3, transition: 'all 0.3s ease' }}>
      {/* Title and Description - Outside Paper */}
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {t('rawDataVisualizationTitle')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('rawDataVisualizationDescription')}
      </Typography>

      <Grid container spacing={3}>
        {/* Chart Selection */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ 
            p: 3, 
            borderRadius: 2,
            backgroundColor: isDarkMode ? '#373737' : '#ffffff'
          }}>
            <Box sx={{ mb: 0 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="chart-select-label">{t('selectChartType')}</InputLabel>
                <Select
                  labelId="chart-select-label"
                  id="chart-select"
                  value={selectedChart}
                  onChange={handleChartChange}
                  label={t('selectChartType')}
                >
                  {chartOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>
        </Grid>

        {/* Statistics Cards */}
        {renderStatsCards()}
        
        {/* Chart Area */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ 
            p: 3, 
            borderRadius: 2,
            backgroundColor: isDarkMode ? '#373737' : '#ffffff'
          }}>
            {loading && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {!loading && !error && chartData && !chartReady && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={30} />
              </Box>
            )}
            
            {!loading && !error && chartData && chartReady && (
              <div key={`chart-container-${selectedChart}-${plotRevision}`}>
                {createChart()}
              </div>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RawDataVisualization; 